import React, {useEffect, useRef, useState} from 'react';
import {CButton} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import Select from "react-select";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import {descreverOperacao, OperacaoExibicao} from "./operationDisplay";

interface OpcaoOperacao {
    label: string,
    value: string
}

interface Props {
    operacoes: Array<any>,
    onChange: (operacoes: Array<any>) => void,
    /** Operações cadastradas, para o "Adicionar passo" */
    opcoes: Array<OpcaoOperacao>,
    operacoesBase: Array<any>,
    onEditar: (operacao: any, index: number) => void,
    /** Identificador estável do passo (o índice muda ao reordenar) */
    idPasso: (operacao: any) => string
}

interface Removido {
    operacao: any,
    index: number
}

const Etiquetas = ({exibicao}: {exibicao: OperacaoExibicao}) => (
    <>
        {exibicao.categorias.map(categoria => (
            <span key={categoria} className="tester-etiqueta">{categoria}</span>
        ))}
        {exibicao.tipo && <span className="tester-etiqueta acao">{exibicao.tipo}</span>}
    </>
);

/**
 * Lista de passos do caso de teste: arrastar ou mover com os botões, editar, excluir (com desfazer
 * até salvar) e adicionar uma operação existente ao fim da lista.
 */
const TestCaseSteps = ({operacoes, onChange, opcoes, operacoesBase, onEditar, idPasso}: Props) => {
    const [removidos, setRemovidos] = useState<Array<Removido>>([]);
    const [aviso, setAviso] = useState<string>('');
    const focoPendenteRef = useRef<string | null>(null);

    // Depois de mover com os botões, o foco acompanha o passo na posição nova
    useEffect(() => {
        const alvo = focoPendenteRef.current;
        if (!alvo) {
            return;
        }
        focoPendenteRef.current = null;
        const [uid] = alvo.split('|');
        const elemento = document.querySelector<HTMLButtonElement>('[data-foco="' + alvo + '"]:not([disabled])')
            || document.querySelector<HTMLButtonElement>('[data-foco="' + uid + '|editar"]');
        elemento?.focus();
    }, [operacoes]);

    const mover = (origem: number, destino: number) => {
        if (destino < 0 || destino >= operacoes.length || origem === destino) {
            return;
        }
        const copia = [...operacoes];
        const [item] = copia.splice(origem, 1);
        copia.splice(destino, 0, item);
        onChange(copia);
        setAviso('Passo "' + descreverOperacao(item).titulo + '" movido para a posição ' + (destino + 1) + '.');
    }

    const moverComBotao = (index: number, direcao: -1 | 1) => {
        focoPendenteRef.current = idPasso(operacoes[index]) + '|' + (direcao < 0 ? 'subir' : 'descer');
        mover(index, index + direcao);
    }

    const aoSoltar = (resultado: DropResult) => {
        if (resultado.destination) {
            mover(resultado.source.index, resultado.destination.index);
        }
    }

    const excluir = (index: number) => {
        const operacao = operacoes[index];
        onChange(operacoes.filter((_, posicao) => posicao !== index));
        setRemovidos(atual => [...atual, {operacao, index}]);
        setAviso('');
    }

    const desfazer = () => {
        const ultimo = removidos[removidos.length - 1];
        if (!ultimo) {
            return;
        }
        const copia = [...operacoes];
        copia.splice(Math.min(ultimo.index, copia.length), 0, ultimo.operacao);
        onChange(copia);
        setRemovidos(atual => atual.slice(0, -1));
        setAviso('Passo "' + descreverOperacao(ultimo.operacao).titulo + '" restaurado na posição ' + (Math.min(ultimo.index, operacoes.length) + 1) + '.');
    }

    const adicionar = (opcao: OpcaoOperacao | null) => {
        if (!opcao) {
            return;
        }
        const base = operacoesBase[parseInt(opcao.value, 10)];
        if (!base) {
            return;
        }
        // Cópia: a mesma operação pode entrar mais de uma vez e cada passo é configurado separadamente
        const nova = {...base, params: {...(base.params || {})}};
        onChange([...operacoes, nova]);
        // O campo continua focado e vazio: dá para ir adicionando um passo atrás do outro
        setAviso('Passo "' + descreverOperacao(nova).titulo + '" adicionado na posição ' + (operacoes.length + 1) + '.');
    }

    const ultimoRemovido = removidos[removidos.length - 1];

    return (
        <section className="tester-painel tester-passos-painel" aria-labelledby="passos-titulo">
            <div className="tester-painel-cabecalho">
                <h2 id="passos-titulo" className="tester-painel-titulo">
                    Passos do teste
                    <span className="tester-contador" aria-label={operacoes.length + ' passos'}>{operacoes.length}</span>
                </h2>
            </div>

            <div className="sr-only" aria-live="polite">{aviso}</div>

            {ultimoRemovido && (
                <div className="tester-desfazer" role="status">
                    <span>
                        Passo <strong>{descreverOperacao(ultimoRemovido.operacao).titulo}</strong> excluído.
                        {removidos.length > 1 && ' ' + removidos.length + ' exclusões podem ser desfeitas.'}
                        {' '}A exclusão só vale depois de confirmar.
                    </span>
                    <CButton size="sm" color="secondary" variant="outline" onClick={desfazer}>
                        <CIcon name="cil-reload" className="mr-1"/>Desfazer
                    </CButton>
                </div>
            )}

            {/* Só a lista rola: o "Adicionar passo" e o resto do formulário ficam sempre à vista */}
            <div className="tester-passos-rolagem">
                {operacoes.length === 0 && (
                    <div className="tester-passos-vazio">
                        <strong>Nenhum passo neste caso de teste</strong>
                        <p className="mb-0">Use o campo abaixo para adicionar as operações na ordem em que o teste deve executá-las.</p>
                    </div>
                )}

                {operacoes.length > 0 && (
                    <DragDropContext onDragEnd={aoSoltar}>
                        <Droppable droppableId="passos">
                            {provided => (
                                <ol className="tester-passos" ref={provided.innerRef} {...provided.droppableProps}>
                                    {operacoes.map((operacao, index) => {
                                        const uid = idPasso(operacao);
                                        const exibicao = descreverOperacao(operacao);
                                        const numero = index + 1;
                                        return (
                                            <Draggable key={uid} draggableId={uid} index={index}>
                                                {(dragProvided, snapshot) => (
                                                    <li
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        className={'tester-passo' + (snapshot.isDragging ? ' arrastando' : '')}
                                                    >
                                                        <span className="tester-passo-alca" {...dragProvided.dragHandleProps}
                                                              aria-label={'Arrastar o passo ' + numero + '. Use espaço para pegar e as setas para mover.'}
                                                              title="Arraste para mudar a ordem">
                                                            <CIcon name="cil-move"/>
                                                        </span>
                                                        <span className="tester-passo-numero" aria-hidden="true">{numero}</span>
                                                        <div className="tester-passo-conteudo">
                                                            <span className="tester-passo-titulo" title={exibicao.titulo}>
                                                                <span className="sr-only">Passo {numero}: </span>{exibicao.titulo}
                                                            </span>
                                                            {(exibicao.categorias.length > 0 || exibicao.tipo) && (
                                                                <span className="tester-passo-etiquetas"><Etiquetas exibicao={exibicao}/></span>
                                                            )}
                                                            {exibicao.detalhes.length > 0 && (
                                                                <span className="tester-passo-detalhes">
                                                                    {exibicao.detalhes.map((detalhe, posicao) => (
                                                                        <span key={posicao} className="tester-passo-detalhe" title={detalhe.rotulo + ': ' + detalhe.valor}>
                                                                            <span>{detalhe.rotulo}:</span>
                                                                            <code>{detalhe.valor}</code>
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="tester-passo-acoes">
                                                            <CButton size="sm" color="secondary" variant="outline" data-foco={uid + '|editar'}
                                                                     onClick={() => onEditar(operacao, index)}
                                                                     aria-label={'Editar o passo ' + numero + ': ' + exibicao.titulo}>
                                                                <CIcon name="cil-pencil" className="mr-1"/>Editar
                                                            </CButton>
                                                            <CButton size="sm" color="secondary" variant="ghost" data-foco={uid + '|subir'}
                                                                     onClick={() => moverComBotao(index, -1)} disabled={index === 0}
                                                                     aria-label={'Mover o passo ' + numero + ' para cima'} title="Mover para cima">
                                                                <CIcon name="cil-arrow-top"/>
                                                            </CButton>
                                                            <CButton size="sm" color="secondary" variant="ghost" data-foco={uid + '|descer'}
                                                                     onClick={() => moverComBotao(index, 1)} disabled={index === operacoes.length - 1}
                                                                     aria-label={'Mover o passo ' + numero + ' para baixo'} title="Mover para baixo">
                                                                <CIcon name="cil-arrow-bottom"/>
                                                            </CButton>
                                                            <CButton size="sm" color="danger" variant="ghost"
                                                                     onClick={() => excluir(index)}
                                                                     aria-label={'Excluir o passo ' + numero + ': ' + exibicao.titulo} title="Excluir passo">
                                                                <CIcon name="cil-trash"/>
                                                            </CButton>
                                                        </div>
                                                    </li>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </ol>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </div>

            <div className="tester-adicionar-passo">
                <label htmlFor="buscaOperacao" className="tester-rotulo d-block">
                    <CIcon name="cil-plus" className="mr-1"/>Adicionar passo ao fim da lista
                </label>
                <Select
                    inputId="buscaOperacao"
                    options={opcoes}
                    value={null}
                    onChange={(opcao: any) => adicionar(opcao)}
                    closeMenuOnSelect
                    blurInputOnSelect={false}
                    formatOptionLabel={(opcao: OpcaoOperacao) => {
                        const exibicao = descreverOperacao(operacoesBase[parseInt(opcao.value, 10)]);
                        return (
                            <span className="d-flex flex-wrap align-items-center" style={{gap: '.35rem'}}>
                                <span>{exibicao.titulo}</span>
                                <Etiquetas exibicao={exibicao}/>
                            </span>
                        );
                    }}
                    placeholder="Buscar operação pelo nome para adicionar"
                    noOptionsMessage={() => 'Nenhuma operação encontrada'}
                    menuPlacement="auto"
                    styles={{menu: (base: any) => ({...base, zIndex: 20})}}
                />
            </div>
        </section>
    );
}

export default TestCaseSteps;
